import {
  FOC_TOUR_LABEL_PLANNING_P_MEETING,
  focFeedbackPopoverHtml,
} from '../focTourFeedback';
import {
  FOC_REQUIRED_DISPOSITION,
  focPlanningPRequirement,
  focPlanningPTourNavVisible,
  type FocPlanningPTourStepDef,
} from './focPlanningPTourTypes';
import { FOC_PLANNING_P_MEETING_TOUR_HREF } from './focPlanningPTourRegistry';

export { FOC_PLANNING_P_MEETING_TOUR_HREF, focPlanningPTourNavVisible };

export const FOC_PLANNING_P_MEETING_TOUR_STEPS: FocPlanningPTourStepDef[] = [
  {
    id: 'sectionIntro',
    phaseIndex: 0,
    title: '10.2 Planning P Meeting Management',
    description:
      'Planning P meetings use IMH-aligned schedules, facilitation workspaces, deliverable links, and deliberate phase progression — not isolated checklist pages.',
  },
  focPlanningPRequirement({
    id: 'pp001',
    requirementId: 'FOC-PP-001',
    name: 'IMH-Based Planning P Schedule',
    disposition: FOC_REQUIRED_DISPOSITION,
    phaseIndex: 0,
    tour: 'planning-stepper',
    description:
      'PRATUS provides a Planning P schedule based on the Coast Guard Incident Management Handbook’s recommended sequence, participants, deliverables, meeting durations, and operational-period planning rhythm. Calendar events are seeded from the IMH phase model when the workspace escalates.',
  }),
  focPlanningPRequirement({
    id: 'pp001a',
    requirementId: 'FOC-PP-001a',
    name: 'Authorized Schedule Adjustment',
    disposition: FOC_REQUIRED_DISPOSITION,
    phaseIndex: 0,
    tour: 'planning-meeting-details',
    openMeetingDetails: true,
    description:
      'Authorized users can adjust meeting timing, duration, participants, deliverables, and operational-period schedule in Meeting Details while retaining the Coast Guard IMH model as the default starting point.',
  }),
  focPlanningPRequirement({
    id: 'pp002',
    requirementId: 'FOC-PP-002',
    name: 'Meeting Facilitation Workspace',
    disposition: FOC_REQUIRED_DISPOSITION,
    phaseIndex: 0,
    tour: 'planning-agenda',
    showAgenda: true,
    description:
      'Each Planning P meeting provides an operational workspace identifying meeting name, operational period, facilitator context, recommended participants, attendance, agenda, expected deliverables, related forms, key decisions, action items, assignments, status, timing, and follow-on work.',
  }),
  focPlanningPRequirement({
    id: 'pp002a',
    requirementId: 'FOC-PP-002a',
    name: 'Meeting Attendance',
    disposition: FOC_REQUIRED_DISPOSITION,
    phaseIndex: 0,
    tour: 'planning-meeting-details',
    openMeetingDetails: true,
    description:
      'PRATUS records meeting attendees, roles, time, and location or distributed-participation context for Initial IC/UC Briefing, Initial Unified Command Meeting, Objectives Meeting, Command and General Staff Meeting, Tactics Meeting, Planning Meeting, Operations Brief, and other authorized incident meetings via Meeting Details and calendar fields.',
  }),
  focPlanningPRequirement({
    id: 'pp002b',
    requirementId: 'FOC-PP-002b',
    name: 'Conclude Meeting',
    disposition: FOC_REQUIRED_DISPOSITION,
    phaseIndex: 0,
    tour: 'planning-complete-phase',
    description:
      'PRATUS provides a visible Complete Phase control as the deliberate, recorded progression past meeting work for the operational period. Completing the phase preserves agenda progress and advances the Planning P rhythm. (A dedicated “Conclude Meeting” label is not separate from Complete Phase today.)',
  }),
  focPlanningPRequirement({
    id: 'pp002c',
    requirementId: 'FOC-PP-002c',
    name: 'Meeting Product Status',
    disposition: FOC_REQUIRED_DISPOSITION,
    phaseIndex: 0,
    tour: 'planning-stepper',
    description:
      'Stepper form status dots distinguish draft readiness for Planning P products. Product vocabulary includes states such as Not Started, In Progress, Draft Ready, Ready for Review, Ready for Approval, Approved, Needs Revision, Final, and Superseded — surfaced through form versioning and rail status indicators.',
  }),
  focPlanningPRequirement({
    id: 'pp003',
    requirementId: 'FOC-PP-003',
    name: 'Deliverable-Driven Workflow',
    disposition: FOC_REQUIRED_DISPOSITION,
    phaseIndex: 0,
    tour: 'planning-form-card',
    widget: 'incidentObjectives',
    description:
      'Planning P meeting views provide direct links to the forms, products, map views, resource information, safety products, organization information, and other deliverables needed for the applicable agenda step via the phase form rail and form panes.',
  }),
  focPlanningPRequirement({
    id: 'pp003a',
    requirementId: 'FOC-PP-003a',
    name: 'Return-to-Context Behavior',
    disposition: FOC_REQUIRED_DISPOSITION,
    phaseIndex: 0,
    description:
      'When a user opens a linked product, map, dashboard, or position workspace from Planning P, PRATUS preserves phase context (including returnPhase on seat workspaces) so facilitators can return to the relevant agenda step.',
  }),
  focPlanningPRequirement({
    id: 'pp003b',
    requirementId: 'FOC-PP-003b',
    name: 'Assignment and Notification',
    disposition: FOC_REQUIRED_DISPOSITION,
    phaseIndex: 0,
    description:
      'When an authorized user identifies a required deliverable, changes its status, or assigns ownership, PRATUS can create trackable work and notify responsible section chiefs or designees through incident actions and notification channels. This walkthrough does not create new assignments.',
  }),
  focPlanningPRequirement({
    id: 'pp003c',
    requirementId: 'FOC-PP-003c',
    name: 'Persistent Active-Selection Indicator',
    disposition: FOC_REQUIRED_DISPOSITION,
    phaseIndex: 0,
    tour: 'planning-forms',
    widget: 'incidentObjectives',
    description:
      'Selecting a form, product, or agenda-linked deliverable keeps a persistent active indication on the Planning P rail and form pane — it remains after hover ends, distinguishes the active item, updates on deliberate selection, and stays consistent while working in the meeting context.',
  }),
  {
    id: 'focFeedback',
    title: 'FOC Feedback Notes',
    description: focFeedbackPopoverHtml(FOC_TOUR_LABEL_PLANNING_P_MEETING),
  },
];

export function focPlanningPMeetingTourStepIds(): string[] {
  return FOC_PLANNING_P_MEETING_TOUR_STEPS.map((step) => step.id);
}
